/**
 * Title Insurance Rate Tables
 *
 * Sources:
 *   PA: TIRBOP Manual, Seventh Revision, effective 08/01/2024
 *       Section 5.50 — Company or Agent Procedure
 *       https://patitleratingbureau.org/section-550/
 *
 *   NJ: NJ Land Title Insurance Rating Bureau, Manual of Rates and Charges
 *       Effective 11/01/2023
 *       https://njlta.org/page/RateManual
 *
 *   NY: TIRSA Rate Manual, Seventh Revision, effective 10/01/2024
 *       Policy rates unchanged since 02/15/2019
 *       https://tirsa.org/rate-manual/
 *
 *   FL: Florida Administrative Code Rule 69O-186.003
 *       Promulgated rates — Florida Office of Insurance Regulation
 *       https://www.law.cornell.edu/regulations/florida/Fla-Admin-Code-Ann-R-69O-186-003
 */

// --- PENNSYLVANIA ---
export const PA = {
  label: 'Pennsylvania',
  code: 'PA',
  simultaneousIncluded: true,
  saleTiers: [
    { upTo: 30000, base: 569.00, perThousand: 0 },
    { upTo: 45000, perThousand: 7.41 },
    { upTo: 100000, perThousand: 6.27 },
    { upTo: 500000, perThousand: 5.70 },
    { upTo: 1000000, perThousand: 4.56 },
    { upTo: 2000000, perThousand: 3.42 },
    { upTo: 7000000, perThousand: 2.28 },
    { upTo: 30000000, perThousand: 1.71 },
    { upTo: Infinity, perThousand: 1.42 },
  ],
  nonSaleTiers: [
    { upTo: 30000, base: 512.00, perThousand: 0 },
    { upTo: 45000, perThousand: 5.98 },
    { upTo: 100000, perThousand: 5.41 },
    { upTo: 500000, perThousand: 4.84 },
    { upTo: 1000000, perThousand: 4.27 },
    { upTo: 2000000, perThousand: 3.13 },
    { upTo: 7000000, perThousand: 1.99 },
    { upTo: 30000000, perThousand: 1.71 },
    { upTo: Infinity, perThousand: 1.42 },
  ],
};

// --- NEW JERSEY ---
export const NJ = {
  label: 'New Jersey',
  code: 'NJ',
  searchAndExamFee: 100.00,
  minimumPremium: 200.00,
  standardTiers: [
    { upTo: 100000, perThousand: 5.25 },
    { upTo: 500000, perThousand: 4.00 },
    { upTo: 2000000, perThousand: 2.75 },
    { upTo: Infinity, perThousand: 2.25 },
  ],
  refinanceTiers: [
    { upTo: 100000, perThousand: 2.50 },
    { upTo: 500000, perThousand: 2.25 },
    { upTo: 2000000, perThousand: 2.00 },
    { upTo: Infinity, perThousand: 1.50 },
  ],
  simultaneousLoanFlat: 25.00,
};

// --- NEW YORK ---
export const NY = {
  label: 'New York',
  code: 'NY',
  zones: {
    zone1: {
      label: 'Zone 1 (Upstate / Western NY)',
      regularOwner: [
        { upTo: 35000, base: 338.00, perThousand: 0 },
        { upTo: 50000, perThousand: 7.52 },
        { upTo: 100000, perThousand: 4.69 },
        { upTo: 500000, perThousand: 3.78 },
        { upTo: 1000000, perThousand: 3.38 },
        { upTo: 5000000, perThousand: 3.09 },
        { upTo: 10000000, perThousand: 2.81 },
        { upTo: 15000000, perThousand: 2.62 },
        { upTo: Infinity, perThousand: 2.36 },
      ],
      regularLoan: [
        { upTo: 35000, base: 284.00, perThousand: 0 },
        { upTo: 50000, perThousand: 6.28 },
        { upTo: 100000, perThousand: 3.90 },
        { upTo: 500000, perThousand: 3.14 },
        { upTo: 1000000, perThousand: 2.81 },
        { upTo: 5000000, perThousand: 2.57 },
        { upTo: 10000000, perThousand: 2.35 },
        { upTo: 15000000, perThousand: 2.19 },
        { upTo: Infinity, perThousand: 1.97 },
      ],
      purchaseOwner: [
        { upTo: 35000, base: 338.00, perThousand: 0 },
        { upTo: 50000, perThousand: 6.73 },
        { upTo: 100000, perThousand: 4.20 },
        { upTo: 500000, perThousand: 3.38 },
        { upTo: 1000000, perThousand: 3.03 },
      ],
      purchaseLoan: [
        { upTo: 35000, base: 284.00, perThousand: 0 },
        { upTo: 50000, perThousand: 6.28 },
        { upTo: 100000, perThousand: 3.90 },
        { upTo: 500000, perThousand: 3.14 },
        { upTo: 1000000, perThousand: 2.81 },
      ],
    },
    zone2: {
      label: 'Zone 2 (Downstate NY / NYC / Long Island)',
      regularOwner: [
        { upTo: 35000, base: 382.00, perThousand: 0 },
        { upTo: 50000, perThousand: 6.34 },
        { upTo: 100000, perThousand: 5.16 },
        { upTo: 500000, perThousand: 4.14 },
        { upTo: 1000000, perThousand: 3.78 },
        { upTo: 5000000, perThousand: 3.48 },
        { upTo: 10000000, perThousand: 3.09 },
        { upTo: 15000000, perThousand: 2.92 },
        { upTo: Infinity, perThousand: 2.62 },
      ],
      regularLoan: [
        { upTo: 35000, base: 327.00, perThousand: 0 },
        { upTo: 50000, perThousand: 5.27 },
        { upTo: 100000, perThousand: 4.31 },
        { upTo: 500000, perThousand: 3.46 },
        { upTo: 1000000, perThousand: 3.14 },
        { upTo: 5000000, perThousand: 2.90 },
        { upTo: 10000000, perThousand: 2.57 },
        { upTo: 15000000, perThousand: 2.42 },
        { upTo: Infinity, perThousand: 2.19 },
      ],
      purchaseOwner: [
        { upTo: 35000, base: 341.70, perThousand: 0 },
        { upTo: 50000, perThousand: 5.67 },
        { upTo: 100000, perThousand: 4.62 },
        { upTo: 500000, perThousand: 3.71 },
        { upTo: 1000000, perThousand: 3.38 },
      ],
      purchaseLoan: [
        { upTo: 35000, base: 327.00, perThousand: 0 },
        { upTo: 50000, perThousand: 5.27 },
        { upTo: 100000, perThousand: 4.31 },
        { upTo: 500000, perThousand: 3.46 },
        { upTo: 1000000, perThousand: 3.14 },
      ],
    },
  },
  simultaneousLoanPercent: 0.30,
  refinanceThreshold: 475000,
  refinanceDiscountAtOrBelow: 0.50,
  refinanceDiscountAbove: 0.30,
};

// --- FLORIDA ---
export const FL = {
  label: 'Florida',
  code: 'FL',
  minimumPremium: 100.00,
  simultaneousLoanFlat: 25.00,
  originalTiers: [
    { upTo: 100000, perThousand: 5.75 },
    { upTo: 1000000, perThousand: 5.00 },
    { upTo: 5000000, perThousand: 2.50 },
    { upTo: 10000000, perThousand: 2.25 },
    { upTo: Infinity, perThousand: 2.00 },
  ],
  refinanceDiscounts: [
    { label: '3 years or under', maxYears: 3, percent: 0.30 },
    { label: 'Over 3 to 4 years', maxYears: 4, percent: 0.40 },
    { label: 'Over 4 to 5 years', maxYears: 5, percent: 0.50 },
    { label: 'Over 5 to 10 years', maxYears: 10, percent: 0.60 },
    { label: 'Over 10 years', maxYears: Infinity, percent: 1.00 },
  ],
};

export const ALL_STATES = { PA, NJ, NY, FL };
